import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Holding, ClientInfo, PlanningChecklist, PortfolioAnalysis, LifetimeIncomeInputs } from '@/types/portfolio';
import { analyzePortfolio } from '@/lib/analysis-engine';
import { PortfolioAssumptions, loadAssumptions, saveAssumptions } from '@/lib/assumptions';
import {
  ScoringConfig,
  loadScoringConfig,
  saveScoringConfig,
  AdviceModel,
  applyRiskToleranceAdjustments
} from '@/lib/scoring-config';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useHoldings } from '@/hooks/useHoldings';
import { useIncomeSources } from '@/hooks/useIncomeSources';
import { usePartner } from '@/hooks/usePartner';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSampleData } from '@/hooks/useSampleData';
import type { ModelMetrics } from '@/components/model';

// Components
import { Header } from './Header';
import { OnboardingWizard } from './OnboardingWizard';
import { buildCardContracts } from '@/domain/cards/buildCards';
import { getCardById } from '@/domain/cards/getCardById';
import { buildActionPlan } from '@/domain/summary/buildActionPlan';
import { ActionPlanPanelRef } from '@/components/portfolio/ActionPlanPanel';
import { toast } from 'sonner';
import { DashboardSkeleton, MobileDashboardSkeleton } from '@/components/ui/loading-skeletons';
import { ErrorState } from '@/components/ui/error-states';
import { DashboardMobileView } from './dashboard/DashboardMobileView';
import { DashboardDesktopView } from './dashboard/DashboardDesktopView';
import { ChatInterface } from '../chat/ChatInterface';

const initialClientInfo: ClientInfo = {
  name: '',
  meetingDate: new Date().toISOString().split('T')[0],
  riskTolerance: 'Moderate',
};

const initialChecklist: PlanningChecklist = {
  willTrust: false,
  healthcareDirectives: false,
  poaDirectives: false,
  emergencyFund: false,
  beneficiaryReview: false,
  executorDesignation: false,
  guardianDesignation: false,
  insuranceCoverage: false,
  digitalAssetPlan: false,
  withdrawalStrategy: false,
  investmentPolicyStatement: false,
};

const initialLifetimeIncomeInputs: LifetimeIncomeInputs = {
  coreLivingExpensesMonthly: 0,
  discretionaryExpensesMonthly: 0,
  healthcareLongTermCareMonthly: 0,
  guaranteedSources: [],
};

export function PortfolioDashboard() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading, updateProfile, currentAge } = useProfile();
  const { holdings: dbHoldings, loading: holdingsLoading, error: holdingsError, fetchHoldings } = useHoldings();
  const { incomeSources, loading: incomeLoading } = useIncomeSources();
  const { partner, partnerHoldings, currentView: partnerView, setCurrentView: setPartnerView, setSamplePartner } = usePartner();
  
  // Local state for holdings (supports both authenticated and unauthenticated use)
  const [localHoldings, setLocalHoldings] = useState<Holding[]>([]);

  // Sample data management (extracted to reduce component complexity)
  const { sampleAccounts, loadSampleData } = useSampleData(setSamplePartner);
  
  // Combine holdings based on partner view mode
  const userHoldings = user ? (dbHoldings.length > 0 ? dbHoldings : localHoldings) : localHoldings;
  const holdings = useMemo(() => {
    if (partnerView === 'household' && partner && partnerHoldings.length > 0) {
      // Combine user and partner holdings for household view
      return [...userHoldings, ...partnerHoldings];
    }
    return userHoldings;
  }, [userHoldings, partnerHoldings, partnerView, partner]);
  
  const [clientInfo, setClientInfo] = useState<ClientInfo>(initialClientInfo);
  const [checklist, setChecklist] = useState<PlanningChecklist>(initialChecklist);
  const [lifetimeIncomeInputs, setLifetimeIncomeInputs] = useState<LifetimeIncomeInputs>(initialLifetimeIncomeInputs);
  const [notes] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Derive activeTab directly from URL pathname (single source of truth)
  const validTabs = ['profile', 'accounts', 'privacy', 'observations', 'holdings', 'model', 'tools', 'advisor-tools', 'learn'];
  const pathTab = location.pathname.replace('/', '') || 'observations';
  const activeTab = validTabs.includes(pathTab) ? pathTab : 'observations';
  const [assumptions, setAssumptions] = useState<PortfolioAssumptions>(() => loadAssumptions());
  const [baseScoringConfig, setBaseScoringConfig] = useState<ScoringConfig>(() => loadScoringConfig());
  const [adviceModel, setAdviceModel] = useState<AdviceModel>('self-directed');
  const [advisorFee, setAdvisorFee] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modelMetrics, setModelMetrics] = useState<ModelMetrics | null>(null);

  // Ref for mobile Action Plan FAB
  const actionPlanRef = useRef<ActionPlanPanelRef>(null);
  const actionPlanContainerRef = useRef<HTMLDivElement>(null);

  // Handle tab change - navigates to clean URL path
  const handleTabChange = useCallback((newTab: string) => {
    if (newTab === 'observations') {
      navigate('/');
    } else {
      navigate(`/${newTab}`);
    }
  }, [navigate]);

  // Sync profile data to clientInfo when profile loads
  useEffect(() => {
    if (profile) {
      setClientInfo(prev => ({
        ...prev,
        name: profile.name || prev.name,
        meetingDate: profile.date_of_birth || prev.meetingDate,
        riskTolerance: profile.risk_tolerance as ClientInfo['riskTolerance'] || prev.riskTolerance,
        currentAge: currentAge || prev.currentAge,
      }));
    }
  }, [profile, currentAge]);

  // Sync income sources from database
  useEffect(() => {
    if (incomeSources.length > 0) {
      setLifetimeIncomeInputs(prev => ({
        ...prev,
        guaranteedSources: incomeSources,
      }));
    }
  }, [incomeSources]);

  // Check if new user needs onboarding
  // Show onboarding if user has no holdings and hasn't completed onboarding
  useEffect(() => {
    if (user && !profileLoading && !holdingsLoading) {
      const hasNoHoldings = dbHoldings.length === 0;
      // Check if onboarding is completed via profile flag
      const onboardingCompleted = profile && !!profile.onboarding_completed_at;

      // Show onboarding for users with no data who haven't completed setup
      if (hasNoHoldings && !onboardingCompleted) {
        setShowOnboarding(true);
      }
    }
  }, [user, profile, profileLoading, holdingsLoading, dbHoldings.length]);

  // Handle holdings update - sync to database if authenticated
  const handleHoldingsUpdate = useCallback((newHoldings: Holding[]) => {
    setLocalHoldings(newHoldings);
  }, []);

  // Apply risk tolerance adjustments to the scoring config
  const scoringConfig = useMemo(() => {
    return applyRiskToleranceAdjustments(baseScoringConfig, clientInfo.riskTolerance);
  }, [baseScoringConfig, clientInfo.riskTolerance]);

  const analysis: PortfolioAnalysis = useMemo(() => {
    return analyzePortfolio(holdings, clientInfo, checklist, scoringConfig, adviceModel, advisorFee, lifetimeIncomeInputs);
  }, [holdings, clientInfo, checklist, scoringConfig, adviceModel, advisorFee, lifetimeIncomeInputs]);

  const cardContracts = useMemo(
    () => (analysis ? buildCardContracts(analysis, holdings) : []),
    [analysis, holdings]
  );

  // Sort cards by severity (RED first, then YELLOW, then GREEN)
  const sortedCards = useMemo(() => {
    const statusOrder = { RED: 0, YELLOW: 1, GREEN: 2 };
    return [...cardContracts].sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
  }, [cardContracts]);

  // Group cards for section headers (neutral language per compliance requirements)
  const observationCards = useMemo(
    () => sortedCards.filter((c) => c.status === 'RED' || c.status === 'YELLOW'),
    [sortedCards]
  );
  const noIssuesCards = useMemo(
    () => sortedCards.filter((c) => c.status === 'GREEN'),
    [sortedCards]
  );

  // Portfolio Snapshot computed values
  const snapshotData = useMemo(() => {
    const totalValue = holdings.reduce((sum, h) => sum + h.shares * h.currentPrice, 0);

    // Count unique accounts from holdings
    const uniqueAccounts = new Set(holdings.map(h => h.accountType || 'Unknown'));
    const accountCount = uniqueAccounts.size || (holdings.length > 0 ? 1 : 0);

    // Count cards with RED or YELLOW status
    const issuesCount = cardContracts.filter(c => c.status === 'RED' || c.status === 'YELLOW').length;

    return {
      totalValue,
      accountCount,
      issuesCount,
      // Use analysis values with safe fallbacks
      expectedReturn: analysis?.expectedReturn ?? 0.07,
      volatility: analysis?.volatility ?? 0.15,
    };
  }, [holdings, cardContracts, analysis?.expectedReturn, analysis?.volatility]);

  const actionPlan = useMemo(() => buildActionPlan(cardContracts, 6), [cardContracts]);

  const selectedCard = useMemo(
    () => getCardById(cardContracts, selectedCategory),
    [cardContracts, selectedCategory]
  );

  const handleAssumptionsChange = useCallback((newAssumptions: PortfolioAssumptions) => {
    setAssumptions(newAssumptions);
    saveAssumptions(newAssumptions);
  }, []);

  const handleScoringConfigChange = useCallback((newConfig: ScoringConfig) => {
    setBaseScoringConfig(newConfig);
    saveScoringConfig(newConfig);
    toast.success('Scoring benchmarks saved');
  }, []);

  // Handle client info changes - sync name/dob/risk to profile if authenticated
  const handleClientInfoChange = useCallback(async (newClientInfo: ClientInfo) => {
    setClientInfo(newClientInfo);
    
    if (user && profile) {
      const updates: Record<string, unknown> = {};
      if (newClientInfo.name !== profile.name) updates.name = newClientInfo.name;
      if (newClientInfo.meetingDate !== profile.date_of_birth) updates.date_of_birth = newClientInfo.meetingDate;
      if (newClientInfo.riskTolerance !== profile.risk_tolerance) updates.risk_tolerance = newClientInfo.riskTolerance;
      
      if (Object.keys(updates).length > 0) {
        await updateProfile(updates as Parameters<typeof updateProfile>[0]);
      }
    }
  }, [user, profile, updateProfile]);

  const handleLoadSample = useCallback(() => {
    const sampleData = loadSampleData();
    setLocalHoldings(sampleData.holdings);
    setClientInfo(prev => ({ ...prev, ...sampleData.clientInfo }));
    setChecklist(sampleData.checklist);
    setLifetimeIncomeInputs(sampleData.lifetimeIncomeInputs);
    toast.success('Sample portfolio loaded');
  }, [loadSampleData]);


  // Show onboarding wizard for new authenticated users
  if (showOnboarding) {
    return <OnboardingWizard onComplete={() => setShowOnboarding(false)} />;
  }

  // Show loading skeleton while data is being fetched
  const isLoading = user && (profileLoading || holdingsLoading || incomeLoading);
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
          {isMobile ? <MobileDashboardSkeleton /> : <DashboardSkeleton />}
        </div>
      </div>
    );
  }

  // Show error state if data fetching failed
  if (user && holdingsError) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <ErrorState
            title="Unable to load your portfolio"
            message={holdingsError}
            onRetry={fetchHoldings}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      {/* Desktop Header */}
      {!isMobile && (
        <Header
          clientInfo={clientInfo}
          analysis={analysis}
          holdings={holdings}
          notes={notes}
          assumptions={assumptions}
          scoringConfig={scoringConfig}
          adviceModel={adviceModel}
          advisorFee={advisorFee}
          partner={partner}
          partnerView={partnerView}
          onPartnerViewChange={setPartnerView}
          onClientInfoChange={handleClientInfoChange}
          onAssumptionsChange={handleAssumptionsChange}
          onScoringConfigChange={handleScoringConfigChange}
          onAdviceModelChange={setAdviceModel}
          onAdvisorFeeChange={setAdvisorFee}
          onLoadSample={handleLoadSample}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          modelMetrics={modelMetrics}
        />
      )}

      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {isMobile ? (
          <DashboardMobileView
            activeTab={activeTab}
            onTabChange={handleTabChange}
            selectedCategory={selectedCategory}
            selectedCard={selectedCard}
            analysis={analysis}
            clientInfo={clientInfo}
            scoringConfig={scoringConfig}
            checklist={checklist}
            lifetimeIncomeInputs={lifetimeIncomeInputs}
            assumptions={assumptions}
            partnerView={partnerView}
            partner={partner}
            sampleAccounts={sampleAccounts}
            onSelectCategory={setSelectedCategory}
            onChecklistUpdate={setChecklist}
            onLifetimeIncomeUpdate={setLifetimeIncomeInputs}
            snapshotData={snapshotData}
            cardContracts={cardContracts}
            actionPlan={actionPlan}
            holdings={holdings}
            onHoldingsUpdate={handleHoldingsUpdate}
            observationCards={observationCards}
            noIssuesCards={noIssuesCards}
            notes={notes}
            actionPlanRef={actionPlanRef}
            actionPlanContainerRef={actionPlanContainerRef}
            settingsOpen={settingsOpen}
            setSettingsOpen={setSettingsOpen}
            adviceModel={adviceModel}
            advisorFee={advisorFee}
            onClientInfoChange={handleClientInfoChange}
            onLoadSample={handleLoadSample}
          />
        ) : (
          <DashboardDesktopView
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            selectedCategory={selectedCategory}
            selectedCard={selectedCard}
            analysis={analysis}
            clientInfo={clientInfo}
            scoringConfig={scoringConfig}
            checklist={checklist}
            lifetimeIncomeInputs={lifetimeIncomeInputs}
            assumptions={assumptions}
            partnerView={partnerView}
            partner={partner}
            sampleAccounts={sampleAccounts}
            onSelectCategory={setSelectedCategory}
            onChecklistUpdate={setChecklist}
            onLifetimeIncomeUpdate={setLifetimeIncomeInputs}
            observationCards={observationCards}
            noIssuesCards={noIssuesCards}
            snapshotData={snapshotData}
            cardContracts={cardContracts}
            actionPlan={actionPlan}
            holdings={holdings}
            onHoldingsUpdate={handleHoldingsUpdate}
            onModelMetricsChange={setModelMetrics}
          />
        )}
      </main>
      <ChatInterface
        portfolioContext={{
          totalValue: analysis.totalValue,
          expectedReturn: analysis.expectedReturn,
          volatility: analysis.volatility,
          sharpeRatio: analysis.sharpeRatio,
          totalFees: analysis.totalFees,
          holdings: holdings.map(h => ({
            ticker: h.ticker,
            name: h.name,
            shares: h.shares,
            value: h.shares * h.currentPrice,
            weight: (h.shares * h.currentPrice) / (analysis.totalValue || 1),
          })),
          // Include model comparison data when available
          modelComparison: modelMetrics ? {
            timeRange: '3yr', // Default, could be made dynamic
            viewMode: 'historical',
            yourPortfolio: modelMetrics.userPortfolio,
            sp500: modelMetrics.benchmark,
            tacticalFund: {
              name: modelMetrics.tactical.name,
              value: modelMetrics.tactical.value,
              totalReturn: modelMetrics.tactical.totalReturn,
            },
          } : undefined,
        }}
      />
    </div>
  );
}
